import React from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import Adam from '../images/login/Adam_login.png'
import Ash from '../images/login/Ash_login.png'
import Lucy from '../images/login/Lucy_login.png'
import Nancy from '../images/login/Nancy_login.png'

/** Characters a guest can pick in the pre-lobby. Names match the in-game character keys. */
export const GUEST_AVATAR_OPTIONS = [
  { name: 'adam', label: 'Adam', img: Adam },
  { name: 'ash', label: 'Ash', img: Ash },
  { name: 'lucy', label: 'Lucy', img: Lucy },
  { name: 'nancy', label: 'Nancy', img: Nancy },
] as const

// Portraits are 48×72 pixel art — keep edges crisp when scaled up
const pixelArt = { imageRendering: 'pixelated' as const, objectFit: 'contain' as const, display: 'block' }

interface GuestAvatarPickerProps {
  value: string
  onChange: (avatar: string) => void
}

export default function GuestAvatarPicker({ value, onChange }: GuestAvatarPickerProps) {
  const selected = GUEST_AVATAR_OPTIONS.find((a) => a.name === value) ?? GUEST_AVATAR_OPTIONS[0]

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, color: 'var(--sevv-text-secondary, #757575)' }}>
        Choose your avatar
      </Typography>

      <Box
        data-testid="guest-avatar-preview"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 1.5,
          py: 1.5,
          borderRadius: 2,
          bgcolor: 'var(--sevv-primary-light, rgba(249, 116, 21, 0.08))',
        }}
      >
        <Box component="img" src={selected.img} alt={selected.label} sx={{ ...pixelArt, width: 64, height: 96 }} />
      </Box>

      <ToggleButtonGroup
        value={selected.name}
        exclusive
        onChange={(_, v) => { if (v) onChange(v) }}
        sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}
      >
        {GUEST_AVATAR_OPTIONS.map((av) => (
          <ToggleButton
            key={av.name}
            value={av.name}
            aria-label={av.label}
            sx={{
              flexDirection: 'column',
              gap: 0.5,
              textTransform: 'none',
              px: 1.5,
              py: 0.75,
              border: '1px solid',
              borderColor: selected.name === av.name ? 'var(--sevv-primary, #f97415)' : '#e0e0e0',
              color: selected.name === av.name ? 'var(--sevv-primary, #f97415)' : 'var(--sevv-text-secondary, #757575)',
              '&.Mui-selected': {
                bgcolor: 'var(--sevv-primary-light, rgba(249, 116, 21, 0.08))',
                color: 'var(--sevv-primary, #f97415)',
              },
            }}
          >
            <Box component="img" src={av.img} alt="" sx={{ ...pixelArt, width: 24, height: 36 }} />
            <Typography variant="caption" sx={{ lineHeight: 1 }}>{av.label}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  )
}
